// src/screens/HistoryOrdersScreen.web.js - Show only completed orders (סופק)
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  RefreshControl,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Platform,
} from 'react-native';
import { useAuth } from '../context/AuthContext';
import { useFirebase } from '../context/FirebaseContext';
import OrderCardWeb from '../components/OrderCard.web';
import { ActivityIndicator } from 'react-native-paper';

// Web-compatible icon component
const WebIcon = ({ name, size = 24, color = '#666' }) => {
  const iconMap = {
    search: '🔍',
    clear: '❌',
    tune: '⚙️',
    check: '✅',
    inbox: '📥',
    wifi: '📶',
    history: '📚',
  };

  return (
    <Text
      style={{
        fontSize: size,
        color,
        textAlign: 'center',
        width: size,
        height: size,
        lineHeight: size,
      }}
    >
      {iconMap[name] || '◯'}
    </Text>
  );
};

const HistoryOrdersScreenWeb = ({ navigation }) => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('הכל');
  const [dateFilter, setDateFilter] = useState('הכל');

  const { user, isLoggingOut } = useAuth();
  const [entityName, setEntityName] = useState('');
  const { getDirectOrdersListener } = useFirebase();
  
  // Use ref to track if component is mounted
  const isMountedRef = useRef(true);
  const unsubscribeRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current && typeof unsubscribeRef.current === 'function') {
        console.log('🧹 Cleaning up HistoryOrders listener on unmount');
        unsubscribeRef.current();
      }
    };
  }, []);

  // Initialize user-facing label
  useEffect(() => {
    if (!user || isLoggingOut) {
      setEntityName('');
      return;
    }

    if (user.belongs === 'זכיין') {
      setEntityName(user.franchiseeName || 'זכיין');
    } else if (user.belongs === 'מנהל סניף') {
      const branch = user.branches?.[0];
      setEntityName(branch?.branchName || 'סניף');
    }
  }, [user, isLoggingOut]);

  // Setup real-time listener for completed orders only
  useEffect(() => {
    // Clear previous listener
    if (unsubscribeRef.current && typeof unsubscribeRef.current === 'function') {
      console.log('🧹 Cleaning up previous HistoryOrders listener');
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // Reset state if logging out or no user
    if (!user || isLoggingOut) {
      console.log('🔄 HistoryOrders: User logged out or logging out, clearing data');
      setOrders([]);
      setFilteredOrders([]);
      setLoading(false);
      setRefreshing(false);
      return;
    }

    // Check if user has branches
    if (!Array.isArray(user.branches) || user.branches.length === 0) {
      console.log('🔄 HistoryOrders: No branches available');
      setOrders([]);
      setFilteredOrders([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubscribe = getDirectOrdersListener(
      user.branches,
      (directOrders) => {
        // Check if component is still mounted and user is still logged in
        if (!isMountedRef.current || isLoggingOut) {
          console.log('🔄 HistoryOrders: Component unmounted or logging out, ignoring update');
          return;
        }

        // ✅ Filter for ONLY "סופק" status (completed orders)
        const completedOrders = (directOrders || []).filter(
          (order) => order.status === 'סופק'
        );

        console.log('📚 HistoryOrders real-time update received:', completedOrders.length, 'completed orders');
        setOrders(completedOrders);
        setFilteredOrders(completedOrders);
        setLoading(false);
        setRefreshing(false);
      },
      (error) => {
        if (!isMountedRef.current || isLoggingOut) {
          console.log('🔄 HistoryOrders: Component unmounted or logging out, ignoring error');
          return;
        }
        
        console.error('❌ HistoryOrders real-time listener error:', error);
        setLoading(false);
        setRefreshing(false);
      }
    );

    // Store the unsubscribe function
    unsubscribeRef.current = unsubscribe;

    // Cleanup function
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        console.log('🧹 Cleaning up HistoryOrders real-time listener');
        unsubscribe();
      }
    };
  }, [user, isLoggingOut, getDirectOrdersListener]);

  // Manual refresh
  const onRefresh = () => {
    if (isLoggingOut) return;
    
    console.log('🔄 HistoryOrders manual refresh triggered');
    setRefreshing(true);
    setTimeout(() => {
      if (isMountedRef.current && !isLoggingOut) {
        setRefreshing(false);
      }
    }, 800);
  };

  // Filters & search for history
  useEffect(() => {
    if (isLoggingOut) return;

    let filtered = [...orders];

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (order) =>
          order.orderNumber?.toLowerCase().includes(q) ||
          order.customerName?.toLowerCase().includes(q) ||
          order.organizationName?.toLowerCase().includes(q) ||
          order.description?.toLowerCase().includes(q)
      );
    }

    // Branch filter
    if (selectedBranch !== 'הכל') {
      filtered = filtered.filter((order) => {
        const orderBranch =
          typeof order.branch === 'string'
            ? order.branch
            : order.branch?.branchName || order.branch?.name || order.branch?.id;
        return orderBranch === selectedBranch;
      });
    }

    // Date range filter for history
    if (dateFilter !== 'הכל') {
      const now = new Date();
      filtered = filtered.filter((order) => {
        const customerOrderDate = new Date(order.customerOrderDate);
        switch (dateFilter) {
          case 'השבוע האחרון': {
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return customerOrderDate >= weekAgo;
          }
          case 'החודש האחרון': {
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            return customerOrderDate >= monthAgo;
          }
          case 'שלושה חודשים אחרונים': {
            const threeMonthsAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            return customerOrderDate >= threeMonthsAgo;
          }
          default:
            return true;
        }
      });
    }

    setFilteredOrders(filtered);
  }, [orders, searchQuery, selectedBranch, dateFilter, isLoggingOut]);

  // Get branch options for filter
  const getBranchOptions = () => {
    if (!user || !user.branches || !Array.isArray(user.branches)) return ['הכל'];
    const branchNames = user.branches.map(branch => 
      typeof branch === 'string' ? branch : branch.branchName || branch.name || branch.id
    );
    return ['הכל', ...branchNames];
  };

  const clearAllFilters = () => {
    if (isLoggingOut) return;
    
    setSearchQuery('');
    setSelectedBranch('הכל');
    setDateFilter('הכל');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchQuery.trim()) count++;
    if (selectedBranch !== 'הכל') count++;
    if (dateFilter !== 'הכל') count++;
    return count;
  };

  const handleOrderPress = (order) => {
    if (isLoggingOut) return;
    
    if (navigation?.navigate) {
      navigation.navigate('OrderDetails', { order, orderType: 'history' });
    }
  };

  // Loading states
  if (!user || isLoggingOut) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.loadingText}>
          {isLoggingOut ? 'מתנתק...' : 'מתנתק...'}
        </Text>
      </View>
    );
  }

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.loadingText}>טוען היסטוריית הזמנות...</Text>
      </View>
    );
  }

  const branchOptions = getBranchOptions();
  const dateFilterOptions = ['הכל', 'השבוע האחרון', 'החודש האחרון', 'שלושה חודשים אחרונים'];

  return (
    <View style={styles.container}>
      {/* Real-time Status Indicator */}
      <View style={styles.realTimeIndicator}>
        <View style={styles.onlineIndicator} />
        <Text style={styles.realTimeText}>היסטוריית הזמנות מושלמות</Text>
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <View style={styles.searchInputContainer}>
          <WebIcon name="search" size={20} color="#666" />
          <TextInput
            style={styles.searchInput}
            placeholder="חיפוש לפי מספר הזמנה, לקוח או ארגון..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#999"
            editable={!isLoggingOut}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')} disabled={isLoggingOut}>
              <WebIcon name="clear" size={20} color="#666" />
            </TouchableOpacity>
          )}
        </View>

        <TouchableOpacity
          style={[styles.filterButton, getActiveFiltersCount() > 0 && styles.activeFilterButton]}
          disabled={isLoggingOut}
        >
          <WebIcon
            name="tune"
            size={20}
            color={getActiveFiltersCount() > 0 ? '#fff' : '#007AFF'}
          />
          {getActiveFiltersCount() > 0 && (
            <View style={styles.filterBadge}>
              <Text style={styles.filterBadgeText}>{getActiveFiltersCount()}</Text>
            </View>
          )}
        </TouchableOpacity>
      </View>

      {/* Quick Filters & Results */}
      <View style={styles.filtersAndResultsContainer}>
        {/* Branch Filter Row */}
        {branchOptions.length > 2 && (
          <View style={styles.filterRowContainer}>
            <Text style={styles.filterRowLabel}>סניף:</Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={styles.filterRowScroll}
              contentContainerStyle={styles.filterRowContent}
            >
              {branchOptions.map((branch) => (
                <QuickFilterChip
                  key={branch}
                  title={branch}
                  isActive={selectedBranch === branch}
                  onPress={() => !isLoggingOut && setSelectedBranch(branch)}
                  disabled={isLoggingOut}
                  color="#9C27B0"
                />
              ))}
            </ScrollView>
          </View>
        )}

        {/* Date Range Filter Row */}
        <View style={styles.filterRowContainer}>
          <Text style={styles.filterRowLabel}>תקופה:</Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.filterRowScroll}
            contentContainerStyle={styles.filterRowContent}
          >
            {dateFilterOptions.map((dateOption) => (
              <QuickFilterChip
                key={dateOption}
                title={dateOption}
                isActive={dateFilter === dateOption}
                onPress={() => !isLoggingOut && setDateFilter(dateOption)}
                color="#4CAF50"
                disabled={isLoggingOut}
              />
            ))}
          </ScrollView>
        </View>

        {/* Results summary */}
        <View style={styles.resultsContainer}>
          <Text style={styles.resultsText}>
            {filteredOrders.length} מתוך {orders.length} הזמנות מושלמות
          </Text>
          {getActiveFiltersCount() > 0 && !isLoggingOut && (
            <TouchableOpacity onPress={clearAllFilters}>
              <Text style={styles.clearFiltersText}>נקה מסננים</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Orders List */}
      <FlatList
        data={filteredOrders}
        keyExtractor={(item) => item.id || item.orderNumber}
        renderItem={({ item }) => (
          <OrderCardWeb order={item} onPress={() => handleOrderPress(item)} />
        )}
        contentContainerStyle={styles.listContainer}
        style={styles.flatListStyle}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl 
            refreshing={refreshing} 
            onRefresh={onRefresh} 
            title="מעדכן..." 
            tintColor="#007AFF"
            enabled={!isLoggingOut}
          />
        }
        scrollEnabled={!isLoggingOut}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <WebIcon name="history" size={64} color="#ccc" />
            <Text style={styles.emptyText}>
              {getActiveFiltersCount() > 0
                ? 'לא נמצאו הזמנות מושלמות התואמות למסננים'
                : 'אין הזמנות מושלמות'}
            </Text>
            {getActiveFiltersCount() > 0 && !isLoggingOut && (
              <TouchableOpacity style={styles.clearFiltersButton} onPress={clearAllFilters}>
                <Text style={styles.clearFiltersButtonText}>נקה מסננים</Text>
              </TouchableOpacity>
            )}
          </View>
        }
      />
    </View>
  );
};

const QuickFilterChip = ({ title, isActive, onPress, color = '#007AFF', disabled = false }) => (
  <TouchableOpacity
    style={[
      styles.filterChip, 
      isActive && [styles.activeFilterChip, { backgroundColor: color, borderColor: color }],
      disabled && styles.disabledChip
    ]}
    onPress={onPress}
    disabled={disabled}
  >
    <Text style={[styles.filterChipText, isActive && styles.activeFilterChipText]}>
      {title}
    </Text>
  </TouchableOpacity>
);

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8f9fa' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 16, fontSize: 16, color: '#666' },
  realTimeIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 4,
    backgroundColor: '#E8F5E8',
    borderBottomWidth: 1,
    borderBottomColor: '#C8E6C9',
  },
  onlineIndicator: { width: 8, height: 8, borderRadius: 4, backgroundColor: '#4CAF50', marginRight: 8 },
  realTimeText: { fontSize: 12, color: '#2E7D32', fontWeight: '500' },
  searchContainer: {
    flexDirection: 'row',
    padding: 12,
    paddingBottom: 4,
    backgroundColor: '#fff',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  searchInputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 12,
    paddingHorizontal: 12,
    marginRight: 12,
  },
  searchInput: { flex: 1, fontSize: 16, paddingVertical: 8, textAlign: 'right', marginHorizontal: 8 },
  filterButton: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: '#f5f5f5',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  activeFilterButton: { backgroundColor: '#007AFF' },
  filterBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#FF3B30',
    borderRadius: 10,
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterBadgeText: { color: '#fff', fontSize: 12, fontWeight: 'bold' },
  filtersAndResultsContainer: {
    backgroundColor: '#fff',
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  filterRowContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#fff',
  },
  filterRowLabel: { fontSize: 12, fontWeight: '600', color: '#333', marginRight: 12, width: 50 },
  filterRowScroll: { flex: 1 },
  filterRowContent: { paddingRight: 16 },
  filterChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#f5f5f5',
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  activeFilterChip: { backgroundColor: '#007AFF', borderColor: '#007AFF' },
  disabledChip: { opacity: 0.6 },
  filterChipText: { fontSize: 13, color: '#666', fontWeight: '500' },
  activeFilterChipText: { color: '#fff' },
  resultsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#f8f9fa',
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  resultsText: { fontSize: 14, color: '#666' },
  clearFiltersText: { fontSize: 14, color: '#007AFF', fontWeight: '500' },
  flatListStyle: { flex: 1 },
  listContainer: { paddingHorizontal: 12, paddingTop: 4, paddingBottom: 20 },
  emptyContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 100 },
  emptyText: { fontSize: 18, color: '#666', textAlign: 'center', marginTop: 16, marginBottom: 24 },
  clearFiltersButton: { backgroundColor: '#007AFF', paddingHorizontal: 24, paddingVertical: 12, borderRadius: 8 },
  clearFiltersButtonText: { color: '#fff', fontSize: 16, fontWeight: '500' },
});

export default HistoryOrdersScreenWeb;